"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.usePosition = exports.translateWithOffset = exports.shouldFlip = exports.placeTooltip = exports.getPosition = exports.getDocumentBoundingRect = exports.POSITION = void 0;

var _react = require("react");

var _useMutationObserver = require("./useMutationObserver");

var _useIsomorphicLayoutEffect = require("./useIsomorphicLayoutEffect");

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

var POSITION = {
  "top-start": "top-start",
  top: "top",
  "top-end": "top-end",
  "bottom-start": "bottom-start",
  bottom: "bottom",
  "bottom-end": "bottom-end",
  "left-start": "left-start",
  left: "left",
  "left-end": "left-end",
  "right-start": "right-start",
  right: "right",
  "right-end": "right-end"
};
exports.POSITION = POSITION;

/**
 * Will position the floating element (tooltip) relative to the anchor element,
 * using `position: fixed` and in such a way that it shouldn't ever appear
 * partially offscreen and will move correctly when the parent is scrolled.
 */
var usePosition = function usePosition(_ref) {
  var position = _ref.position,
      _ref$offset = _ref.offset,
      offset = _ref$offset === void 0 ? 0 : _ref$offset,
      _ref$active = _ref.active,
      active = _ref$active === void 0 ? true : _ref$active;

  var _useState = (0, _react.useState)({
    isFlipped: false
  }),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];

  var tooltipRef = (0, _react.useRef)(null);
  var anchorRef = (0, _react.useRef)(null); // Re-position when there's any change to the tooltip

  (0, _useMutationObserver.useMutationObserver)({
    ref: tooltipRef,
    onMutate: function onMutate() {
      if (!tooltipRef.current || !anchorRef.current) return;
      var tooltip = tooltipRef.current;
      var anchor = anchorRef.current;
      setState(placeTooltip(anchor, tooltip, position, offset));
    }
  });
  (0, _useIsomorphicLayoutEffect.useIsomorphicLayoutEffect)(function () {
    if (!tooltipRef.current || !anchorRef.current) return;
    var tooltip = tooltipRef.current;
    var anchor = anchorRef.current;
    tooltip.style.position = "fixed";
    tooltip.style.top = "0";
    tooltip.style.left = "0";

    var handleScroll = function handleScroll() {
      if (!active) return;
      setState(placeTooltip(anchor, tooltip, position, offset));
    };

    if (active) {
      document.addEventListener("scroll", handleScroll, {
        passive: true
      });
    }

    var handleResize = function handleResize() {
      setState(placeTooltip(anchor, tooltip, position, offset));
    };

    window.addEventListener("resize", handleResize, {
      passive: true
    });
    setState(placeTooltip(anchor, tooltip, position, offset));
    return function () {
      document.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [active, tooltipRef, anchorRef]);
  return {
    // Element that floating element is anchored to
    tooltipRef: tooltipRef,
    // Element you want to position relative to the anchor
    anchorRef: anchorRef,
    state: state
  };
};

exports.usePosition = usePosition;

var placeTooltip = function placeTooltip(anchor, tooltip, position) {
  var offset = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
  var boundaryRect = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : getDocumentBoundingRect();
  var elementRect = anchor.getBoundingClientRect();
  var tooltipRect = tooltip.getBoundingClientRect();
  var targetPosition = getPosition(elementRect, tooltipRect, position); // Flip to avoid edges

  var isFlipped = shouldFlip(targetPosition, position, boundaryRect, tooltipRect);

  if (isFlipped) {
    position = getOppositePosition(position);
    targetPosition = getPosition(elementRect, tooltipRect, position);
  } // Clamp position within boundary


  targetPosition.x = Math.max(boundaryRect.left, targetPosition.x);
  targetPosition.x = Math.min(boundaryRect.right - tooltipRect.width, targetPosition.x);
  targetPosition.y = Math.max(boundaryRect.top, targetPosition.y);
  targetPosition.y = Math.min(boundaryRect.bottom - tooltipRect.height, targetPosition.y); // Should hide entirely if it scrolls out of view

  var shouldHide = !isWithin(elementRect, boundaryRect);
  tooltip.style.display = shouldHide ? "none" : "block";
  tooltip.style.transform = translateWithOffset(targetPosition, position, offset);
  return {
    isFlipped: isFlipped
  };
};

exports.placeTooltip = placeTooltip;

var getPosition = function getPosition(elementRect, tooltipRect, position) {
  var x;
  var y;

  switch (position) {
    case "top-start":
    case "bottom-start":
      x = elementRect.left;
      break;

    case "top":
    case "bottom":
      x = elementRect.left + elementRect.width / 2 - tooltipRect.width / 2;
      break;

    case "top-end":
    case "bottom-end":
      x = elementRect.right - tooltipRect.width;
      break;

    case "left-start":
    case "left":
    case "left-end":
      // [..] {XXX}
      x = elementRect.left - tooltipRect.width;
      break;

    case "right-start":
    case "right":
    case "right-end":
      // {XXX} [...]
      x = elementRect.right;
      break;
  }

  switch (position) {
    case "left-start":
    case "right-start":
      y = elementRect.top;
      break;

    case "left":
    case "right":
      y = elementRect.top + elementRect.height / 2 - tooltipRect.height / 2;
      break;

    case "left-end":
    case "right-end":
      y = elementRect.bottom - tooltipRect.height;
      break;

    case "top-start":
    case "top":
    case "top-end":
      // [..]
      // {XXX}
      y = elementRect.top - tooltipRect.height;
      break;

    case "bottom-start":
    case "bottom":
    case "bottom-end":
      // {XXX}
      // [...]
      y = elementRect.bottom;
      break;
  }

  return {
    x: x,
    y: y
  };
};

exports.getPosition = getPosition;

var translateWithOffset = function translateWithOffset(targetPosition, position, offset) {
  var _ref2 = function () {
    switch (position) {
      case "top-start":
        return [targetPosition.x, targetPosition.y - offset];

      case "top":
        return [targetPosition.x, targetPosition.y - offset];

      case "top-end":
        return [targetPosition.x, targetPosition.y - offset];

      case "bottom-start":
        return [targetPosition.x, targetPosition.y + offset];

      case "bottom":
        return [targetPosition.x, targetPosition.y + offset];

      case "bottom-end":
        return [targetPosition.x, targetPosition.y + offset];

      case "left-start":
        return [targetPosition.x - offset, targetPosition.y];

      case "left":
        return [targetPosition.x - offset, targetPosition.y];

      case "left-end":
        return [targetPosition.x - offset, targetPosition.y];

      case "right-start":
        return [targetPosition.x + offset, targetPosition.y];

      case "right":
        return [targetPosition.x + offset, targetPosition.y];

      case "right-end":
        return [targetPosition.x + offset, targetPosition.y];
    }
  }(),
      _ref3 = _slicedToArray(_ref2, 2),
      x = _ref3[0],
      y = _ref3[1];

  return "translate(".concat(x, "px, ").concat(y, "px)");
};

exports.translateWithOffset = translateWithOffset;

var getOppositePosition = function getOppositePosition(position) {
  switch (position) {
    case "top-start":
      return "bottom-start";

    case "top":
      return "bottom";

    case "top-end":
      return "bottom-end";

    case "bottom-start":
      return "top-start";

    case "bottom":
      return "top";

    case "bottom-end":
      return "top-end";

    case "left-start":
      return "right-start";

    case "left":
      return "right";

    case "left-end":
      return "right-end";

    case "right-start":
      return "left-start";

    case "right":
      return "left";

    case "right-end":
      return "left-end";
  }
};

var getDocumentBoundingRect = function getDocumentBoundingRect() {
  var width = document.body.clientWidth;
  var height = document.body.clientHeight;
  return {
    top: 0,
    left: 0,
    right: width,
    bottom: height,
    width: width,
    height: height
  };
};

exports.getDocumentBoundingRect = getDocumentBoundingRect;

var shouldFlip = function shouldFlip(targetPosition, position, boundaryRect, tooltipRect) {
  switch (position) {
    case "top-start":
    case "top":
    case "top-end":
      return targetPosition.y < boundaryRect.top;

    case "bottom-start":
    case "bottom":
    case "bottom-end":
      return targetPosition.y + tooltipRect.height > boundaryRect.bottom;

    case "left-start":
    case "left":
    case "left-end":
      return targetPosition.x < boundaryRect.left;

    case "right-start":
    case "right":
    case "right-end":
      return targetPosition.x + tooltipRect.width > boundaryRect.right;
  }
};

exports.shouldFlip = shouldFlip;

var isWithin = function isWithin(elementRect, boundaryRect) {
  return boundaryRect.top < elementRect.bottom && boundaryRect.left < elementRect.right && boundaryRect.bottom > elementRect.top && boundaryRect.right > elementRect.left;
};
//# sourceMappingURL=usePosition.js.map